import { useState, useEffect } from 'react';
import axios from '@/utils/api';
import { toast } from 'sonner';
import { 
  CreditCard, Crown, Users, ExternalLink, RefreshCw,
  ChevronDown, Search, Filter, DollarSign, Calendar, XCircle, Loader2,
  TrendingUp, TrendingDown, RotateCcw, AlertTriangle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { format } from 'date-fns';

// Use same-origin API path on production custom domains
const API_BASE = window.location.hostname.includes('preview.emergentagent.com') || window.location.hostname === 'localhost'
  ? (process.env.REACT_APP_BACKEND_URL || '')
  : '';

// Default tier config - labels will be fetched dynamically
const DEFAULT_TIER_CONFIG = {
  founding_member: { defaultLabel: 'Founding Member', color: 'text-yellow-400', bg: 'bg-yellow-500/20', icon: '👑' },
  premium: { defaultLabel: 'Premium', color: 'text-green-400', bg: 'bg-green-500/20', icon: '⭐' },
  free: { defaultLabel: 'Free', color: 'text-gray-400', bg: 'bg-gray-500/20', icon: '○' },
  enterprise: { defaultLabel: 'Enterprise', color: 'text-purple-400', bg: 'bg-purple-500/20', icon: '🏢' },
};

const SubscriptionManager = ({ users: propUsers = [], onRefresh }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTier, setFilterTier] = useState('all');
  const [tierLabels, setTierLabels] = useState(null);
  const [revenueAnalytics, setRevenueAnalytics] = useState(null);
  const [loadingRevenue, setLoadingRevenue] = useState(true);
  const [refundingUser, setRefundingUser] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    founding_member: 0,
    premium: 0,
    free: 0,
    enterprise: 0,
  });

  // Fetch tier labels
  useEffect(() => {
    axios.get(`${API_BASE}/api/admin/settings/tier-labels`)
      .then(res => setTierLabels(res.data.labels))
      .catch(() => setTierLabels(null));
  }, []);

  // Fetch revenue analytics
  useEffect(() => {
    fetchRevenueAnalytics();
  }, []);

  const fetchRevenueAnalytics = async () => {
    setLoadingRevenue(true);
    try {
      const res = await axios.get(`${API_BASE}/api/admin/revenue-analytics`, {
        withCredentials: true
      });
      setRevenueAnalytics(res.data);
    } catch (error) {
      console.error('Failed to load revenue analytics:', error);
    } finally {
      setLoadingRevenue(false);
    }
  };

  // Helper to get tier label
  const getTierLabel = (tier) => {
    if (tierLabels && tierLabels[tier]) return tierLabels[tier];
    return DEFAULT_TIER_CONFIG[tier]?.defaultLabel || tier;
  };

  // Use prop users or fetch our own
  useEffect(() => {
    if (propUsers && propUsers.length > 0) {
      setUsers(propUsers);
      calculateStats(propUsers);
      setLoading(false);
    } else {
      fetchUsers();
    }
  }, [propUsers]);

  const calculateStats = (userData) => {
    const tierCounts = {
      total: userData.length,
      founding_member: userData.filter(u => u.membership_tier === 'founding_member').length,
      premium: userData.filter(u => u.membership_tier === 'premium').length,
      free: userData.filter(u => u.membership_tier === 'free' || !u.membership_tier).length,
      enterprise: userData.filter(u => u.membership_tier === 'enterprise').length,
    };
    setStats(tierCounts);
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/admin/users`, {
        withCredentials: true
      });
      
      const userData = res.data.users || res.data || [];
      setUsers(userData);
      calculateStats(userData);
    } catch (error) {
      console.error('Failed to load users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh();
    } else {
      fetchUsers();
    }
  };

  const handleChangeTier = async (userId, newTier) => {
    try {
      await axios.post(
        `${API_BASE}/api/admin/users/${userId}/membership`,
        { membership_tier: newTier },
        { withCredentials: true }
      );
      toast.success(`User updated to ${getTierLabel(newTier)}`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update membership');
    }
  };

  const [cancellingUser, setCancellingUser] = useState(null);

  const handleCancelSubscription = async (userId, email) => {
    if (!confirm(`Cancel subscription for ${email}? This will:\n• End their premium access immediately\n• Downgrade them to Free tier\n• They will NOT be refunded automatically`)) {
      return;
    }
    
    setCancellingUser(userId);
    try {
      await axios.post(
        `${API_BASE}/api/admin/users/${userId}/cancel-subscription`,
        {},
        { withCredentials: true }
      );
      toast.success(`Subscription cancelled for ${email}`);
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to cancel subscription';
      toast.error(message);
    } finally {
      setCancellingUser(null);
    }
  };

  const handleRefund = async (userId, email) => {
    const amountStr = prompt(`Refund for ${email}\n\nEnter amount to refund (leave blank for full refund):\nExample: 9.99`);
    
    if (amountStr === null) return; // Cancelled
    
    const amount = amountStr.trim() ? parseFloat(amountStr) : null;
    
    if (amountStr.trim() && (isNaN(amount) || amount <= 0)) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    if (!confirm(`Confirm refund${amount ? ` of $${amount.toFixed(2)}` : ' (full amount)'} for ${email}?`)) {
      return;
    }
    
    setRefundingUser(userId);
    try {
      const res = await axios.post(
        `${API_BASE}/api/admin/users/${userId}/refund`,
        { amount, reason: 'requested_by_customer' },
        { withCredentials: true }
      );
      toast.success(res.data.message || `Refunded $${res.data.amount} successfully`);
      fetchRevenueAnalytics(); // Refresh revenue stats
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to process refund';
      toast.error(message);
    } finally {
      setRefundingUser(null);
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = !searchQuery || 
      user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTier = filterTier === 'all' || user.membership_tier === filterTier;
    return matchesSearch && matchesTier;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-[#FF7901]" />
            Subscription Management
          </h2>
          <p className="text-sm text-[#FDF2F8]/60 mt-1">
            Manage user tiers and view subscription status
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={handleRefresh}
            variant="outline"
            size="sm"
            className="border-[#4A0A66] text-[#FDF2F8]/70"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <a
            href="https://dashboard.stripe.com/subscriptions"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-[#635BFF] hover:bg-[#5851ea] text-white rounded-lg text-sm font-medium transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            Open Stripe
          </a>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-[#4A0A66]/20 border border-[#4A0A66]/40 rounded-xl p-4">
          <div className="flex items-center gap-2 text-[#FDF2F8]/60 text-sm mb-1">
            <Users className="w-4 h-4" />
            Total Users
          </div>
          <p className="text-2xl font-bold text-white">{stats.total}</p>
        </div>
        {Object.entries(DEFAULT_TIER_CONFIG).map(([tier, config]) => (
          <div key={tier} className="bg-[#4A0A66]/20 border border-[#4A0A66]/40 rounded-xl p-4">
            <div className={`flex items-center gap-2 text-sm mb-1 ${config.color}`}>
              <span>{config.icon}</span>
              {getTierLabel(tier)}
            </div>
            <p className="text-2xl font-bold text-white">{stats[tier] || 0}</p>
          </div>
        ))}
      </div>

      {/* Revenue Analytics */}
      <div className="bg-gradient-to-r from-green-900/20 to-emerald-900/20 border border-green-500/30 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-400" />
            Revenue Analytics
          </h3>
          <Button
            onClick={fetchRevenueAnalytics}
            variant="ghost"
            size="sm"
            className="text-green-400 hover:text-green-300"
            disabled={loadingRevenue}
          >
            {loadingRevenue ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
        
        {loadingRevenue ? (
          <div className="flex justify-center py-4">
            <Loader2 className="w-6 h-6 animate-spin text-green-400" />
          </div>
        ) : revenueAnalytics ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-black/20 rounded-lg p-3">
              <p className="text-xs text-green-400/70 mb-1">Monthly Recurring Revenue</p>
              <p className="text-2xl font-bold text-green-400">${revenueAnalytics.mrr?.toLocaleString()}</p>
            </div>
            <div className="bg-black/20 rounded-lg p-3">
              <p className="text-xs text-[#FDF2F8]/50 mb-1">Revenue (Last 30 Days)</p>
              <p className="text-2xl font-bold text-white">${revenueAnalytics.total_revenue_30d?.toLocaleString()}</p>
            </div>
            <div className="bg-black/20 rounded-lg p-3">
              <p className="text-xs text-[#FDF2F8]/50 mb-1">Active Subscribers</p>
              <p className="text-2xl font-bold text-white">{revenueAnalytics.active_subscribers}</p>
              <p className="text-xs text-[#FDF2F8]/40">
                {revenueAnalytics.premium_count} premium · {revenueAnalytics.enterprise_count} enterprise
              </p>
            </div>
            <div className="bg-black/20 rounded-lg p-3">
              <p className="text-xs text-[#FDF2F8]/50 mb-1 flex items-center gap-1">
                Churn Rate (30d)
                {revenueAnalytics.churn_rate > 5 && <AlertTriangle className="w-3 h-3 text-yellow-400" />}
              </p>
              <p className={`text-2xl font-bold ${revenueAnalytics.churn_rate > 5 ? 'text-yellow-400' : 'text-white'}`}>
                {revenueAnalytics.churn_rate}%
              </p>
              <p className="text-xs text-[#FDF2F8]/40">
                {revenueAnalytics.churned_30d} churned · {revenueAnalytics.new_subscribers_30d} new
              </p>
            </div>
          </div>
        ) : (
          <p className="text-center text-[#FDF2F8]/50 py-4">Unable to load revenue data</p>
        )}
      </div>

      {/* Pricing Info */}
      <div className="bg-gradient-to-r from-[#4A0A66]/30 to-[#df1283]/10 border border-[#4A0A66]/40 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-white font-semibold flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-green-400" />
              Current Pricing
            </h3>
            <p className="text-sm text-[#FDF2F8]/60 mt-1">
              Manage pricing and products in Stripe Dashboard
            </p>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-white">$9.99</p>
              <p className="text-xs text-[#FDF2F8]/50">/ month</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-white">$75</p>
              <p className="text-xs text-[#FDF2F8]/50">/ year</p>
            </div>
            <a
              href="https://dashboard.stripe.com/products"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-[#FF7901] hover:text-[#df1283] flex items-center gap-1"
            >
              Edit in Stripe <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#FDF2F8]/40" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name or email..."
            className="pl-10 bg-[#1a0527] border-[#4A0A66] text-white"
          />
        </div>
        <select
          value={filterTier}
          onChange={(e) => setFilterTier(e.target.value)}
          className="bg-[#1a0527] border border-[#4A0A66] text-white rounded-md px-4 py-2 text-sm"
        >
          <option value="all">All Tiers</option>
          <option value="founding_member">{getTierLabel('founding_member')}s</option>
          <option value="premium">{getTierLabel('premium')}</option>
          <option value="free">{getTierLabel('free')}</option>
          <option value="enterprise">{getTierLabel('enterprise')}</option>
        </select>
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin w-8 h-8 border-2 border-[#df1283] border-t-transparent rounded-full" />
        </div>
      ) : (
        <div className="bg-[#4A0A66]/20 border border-[#4A0A66]/40 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#4A0A66]/40">
                <th className="text-left p-4 text-sm font-medium text-[#FDF2F8]/60">User</th>
                <th className="text-left p-4 text-sm font-medium text-[#FDF2F8]/60">Current Tier</th>
                <th className="text-left p-4 text-sm font-medium text-[#FDF2F8]/60">Joined</th>
                <th className="text-left p-4 text-sm font-medium text-[#FDF2F8]/60">Stripe</th>
                <th className="text-right p-4 text-sm font-medium text-[#FDF2F8]/60">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => {
                const tierConfig = DEFAULT_TIER_CONFIG[user.membership_tier] || DEFAULT_TIER_CONFIG.free;
                return (
                  <tr key={user.user_id} className="border-b border-[#4A0A66]/20 hover:bg-[#4A0A66]/10">
                    <td className="p-4">
                      <div>
                        <p className="font-medium text-white">{user.name || 'Unknown'}</p>
                        <p className="text-sm text-[#FDF2F8]/50">{user.email}</p>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm ${tierConfig.bg} ${tierConfig.color}`}>
                        {tierConfig.icon} {getTierLabel(user.membership_tier || 'free')}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-[#FDF2F8]/60">
                      {user.created_at ? format(new Date(user.created_at), 'MMM d, yyyy') : '-'}
                    </td>
                    <td className="p-4">
                      {user.stripe_customer_id ? (
                        <a
                          href={`https://dashboard.stripe.com/customers/${user.stripe_customer_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-[#635BFF] hover:underline flex items-center gap-1"
                        >
                          View <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <span className="text-sm text-[#FDF2F8]/30">No Stripe account</span>
                      )}
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <select
                          value={user.membership_tier || 'free'}
                          onChange={(e) => handleChangeTier(user.user_id, e.target.value)}
                          className="bg-[#1a0527] border border-[#4A0A66] text-white rounded-md px-3 py-1.5 text-sm"
                        >
                          <option value="free">{getTierLabel('free')}</option>
                          <option value="premium">{getTierLabel('premium')}</option>
                          <option value="founding_member">{getTierLabel('founding_member')}</option>
                          <option value="enterprise">{getTierLabel('enterprise')}</option>
                        </select>
                        {/* Refund button - show for users with Stripe account */}
                        {user.stripe_customer_id && (
                          <Button
                            onClick={() => handleRefund(user.user_id, user.email)}
                            disabled={refundingUser === user.user_id}
                            variant="ghost"
                            size="sm"
                            className="text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/20"
                            title="Issue refund"
                          >
                            {refundingUser === user.user_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <RotateCcw className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                        {/* Cancel subscription button */}
                        {user.membership_tier === 'premium' && user.stripe_customer_id && (
                          <Button
                            onClick={() => handleCancelSubscription(user.user_id, user.email)}
                            disabled={cancellingUser === user.user_id}
                            variant="ghost"
                            size="sm"
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                            title="Cancel Stripe subscription"
                          >
                            {cancellingUser === user.user_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <XCircle className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          
          {filteredUsers.length === 0 && (
            <div className="text-center py-12 text-[#FDF2F8]/50">
              No users found matching your criteria
            </div>
          )}
        </div>
      )}

      {/* Info Box */}
      <div className="bg-[#1a0527] border border-[#4A0A66]/40 rounded-xl p-4 text-sm text-[#FDF2F8]/60">
        <p className="font-medium text-white mb-2">💡 Subscription Management:</p>
        <ul className="space-y-1 list-disc list-inside">
          <li><strong>Change Tier</strong> — Use dropdown to change user's access level instantly</li>
          <li><strong>Refund</strong> — Click <RotateCcw className="w-3 h-3 inline text-yellow-400" /> to issue a full or partial refund for their last payment</li>
          <li><strong>Cancel Subscription</strong> — Click <XCircle className="w-3 h-3 inline text-red-400" /> to cancel and downgrade to Free</li>
          <li><strong>View in Stripe</strong> — Click "View" to see full billing details in Stripe Dashboard</li>
        </ul>
        <p className="mt-3 text-[#FDF2F8]/40">
          Failed payments will automatically email users to update their payment method.
        </p>
      </div>
    </div>
  );
};

export default SubscriptionManager;
